import React, { useState } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { reportError } from '@modernloop/shared/observability';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { useSnackbar } from 'notistack';

import AtsName from 'src/entities/Ats/AtsName';
import AtsScorecardName from 'src/entities/Ats/AtsScorecardName';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useDirectoryInfo from 'src/hooks/useDirectoryInfo';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import cancelInterview from 'src/utils/api/cancelInterview';

type Props = {
  applicationStageInterviewId: string;
  onClose: () => void;
};

const ScheduleContentCancelInterviewDialog = ({ applicationStageInterviewId, onClose }: Props) => {
  const { t } = useTranslation();
  const directoryInfo = useDirectoryInfo();

  const [canceling, setCanceling] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const handleCancelEvent = async () => {
    setCanceling(true);
    // eslint-disable-next-line promise/catch-or-return
    cancelInterview({ applicationStageInterviewId })
      .then(() => {
        refetchScheduleTaskQuery();
        refetchActivityFeed();
        onClose();
        return undefined; // Returning to prevent eslint error
      })
      .catch((error) => {
        reportError(error);
        enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      })
      .finally(() => setCanceling(false));
  };

  return (
    <Dialog
      open
      title={t('hiring-dashboard-schedule:schedule.cancelDialog.title')}
      subTitle={t('hiring-dashboard-schedule:schedule.cancelDialog.subtitle')}
      onClose={onClose}
      submitOptions={{
        label: t('hiring-dashboard-schedule:schedule.content.cancelEvent'),
        onClick: handleCancelEvent,
        isDangerous: true,
        isDisabled: canceling,
        isLoading: canceling,
      }}
      cancelOptions={{
        label: t('hiring-dashboard-schedule:schedule.content.keepEvent'),
        onClick: onClose,
        isDisabled: canceling,
      }}
    >
      <Trans
        i18nKey="hiring-dashboard-schedule:schedule.cancelInterview.deleteWarning"
        values={{ calendarName: directoryInfo.label }}
        components={{ AtsName: <AtsName />, AtsScorecardName: <AtsScorecardName /> }}
      />
    </Dialog>
  );
};

export default ScheduleContentCancelInterviewDialog;
